import { ValidationError } from "sequelize"
import {
    MtTask,
    MtTaskCreate,
    MtTaskSave,
    MtTaskUpdateStatus,
    MtTaskUpdateLifecyclePhase,
    MtTaskDelegate,
    MtTaskTag,
    MtTaskAttachment,
    MtDriveFile,
    SaveMtTaskTagType,
    SaveMtTaskAttachmentType,
} from "server/models"
import { PENDING, IN_PROGRESS, SUPPOSEDLY_COMPLETED, COMPLETED, DEFERRED } from "const"
import { OrderType, TaskStatusType } from "client/utils"
import {
    GET, POST, DELETE, sort, search, validate, filterByDeadline,
} from "client/store/utils"
import {
    LangType,
    SaveTaskObserverList,
    SaveTaskParticipantList,
} from "client/store"
import {
    TaskList,
    TaskFilters,
    TaskValidation,
} from "./types"

export const LoadAllTasks = (
    accountId: number,
    projectId: number,
    userId: number,
) => (
    GET(
        projectId
            ? `/mtTask/projectId/${projectId}/userId/${userId}`
            : `/mtTask/accountId/${accountId}/userId/${userId}`,
    ).then((tasks: MtTask[]) => {
        const taskList: TaskList = {}
        tasks.forEach((task) => {
            taskList[task.id] = task
        })
        return taskList
    })
)

export const LoadTaskById = (
    taskId: number,
    accountId: number,
) => (
    GET(`/mtTask/id/${taskId}/accountId/${accountId}`)
    .then((task: MtTask) => task)
)

export const SaveTask = (
    id: number,
    task: MtTaskCreate,
    participants: readonly number[],
    observers: readonly number[],
    attachments: readonly MtDriveFile[],
    tagIds: number[],
    lang: LangType,
) => {
    const data: MtTaskSave = {
        id,
        task,
    }
    return POST(
        "/mtTask/save",
        JSON.stringify(data),
    ).then((res) => {
        const newTask = res as MtTask
        const error = res as ValidationError

        const newTaskId = newTask.id || null
        const validation = <TaskValidation>validate(error, lang)

        if (validation) {
            return {
                newTaskId,
                validation,
                tags: [],
            }
        }

        const attachmentData: MtTaskAttachment[] = []
        attachments.forEach((file, index) => {
            attachmentData.push({
                taskId: newTaskId || id,
                fileId: file.id,
                order: index,
            })
        })

        const tags: MtTaskTag[] = []
        tagIds.forEach((tagId) => {
            tags.push({
                taskId: newTaskId || id,
                tagId,
            })
        })

        return Promise.all([
            SaveTaskParticipantList(participants, newTaskId || id),
            SaveTaskObserverList(observers, newTaskId || id),
            SaveTaskAttachments(newTaskId || id, attachmentData),
            SaveTaskTags(newTaskId || id, tags),
        ]).then(() => ({
            newTaskId,
            validation,
            tags,
        }))
    })
}

export const UpdateTaskStatus = (
    id: number,
    status: TaskStatusType,
) => {
    const data: MtTaskUpdateStatus = {
        id,
        status,
        statusChanged: new Date(),
    }
    return POST(
        "/mtTask/updateStatus",
        JSON.stringify(data),
    ).then((res: number) => res)
}

export const ClearTaskLifecyclePhase = (phaseId: number) => (
    GET(`/mtTask/clearLifecyclePhase/${phaseId}`)
    .then((res: number) => res)
)

export const UpdateTaskLifecyclePhase = (
    id: number,
    phaseId: number,
) => {
    const data: MtTaskUpdateLifecyclePhase = {
        id,
        phaseId,
    }
    return POST(
        "/mtTask/updateLifecyclePhase",
        JSON.stringify(data),
    ).then((res: number) => res)
}

export const DelegateTask = (
    id: number,
    responsible: number,
) => {
    const data: MtTaskDelegate = {
        id,
        responsible,
    }
    return POST(
        "/mtTask/delegate",
        JSON.stringify(data),
    ).then((res: number) => res)
}

export const ClearParentsTask = (taskId: number, accountId: number) => (
    GET(`/mtTask/clearParents/taskId/${taskId}/accountId/${accountId}`)
)

export const DeleteTask = (
    taskId: number,
    accountId: number,
) => (
    DELETE(`/mtTask/delete/${taskId}/${accountId}`)
    .then((removedRows: number) => removedRows)
)

export const SortTasks = (
    tasks: MtTask[],
    sortColumn: keyof MtTask,
    sortOrder: OrderType,
    filters: TaskFilters,
) => {
    const tasksFiltered = tasks.filter((task) => {
        let showRow = true
        const statusArray: TaskStatusType[] = []
        if (filters.status) {
            if (filters.status.pending) statusArray.push(PENDING)
            if (filters.status.inProgress) statusArray.push(IN_PROGRESS)
            if (filters.status.pendingReview) statusArray.push(SUPPOSEDLY_COMPLETED)
            if (filters.status.completed) statusArray.push(COMPLETED)
            if (filters.status.deferred) statusArray.push(DEFERRED)
        }

        if (
            filters.name
            && filters.name.trim().length
            && task.name.toUpperCase().indexOf(filters.name.trim().toUpperCase()) < 0
        ) showRow = false

        else if (filters.projectId && task.projectId !== filters.projectId) showRow = false
        else if (filters.creator && task.creator !== filters.creator) showRow = false
        else if (filters.responsible && task.responsible !== filters.responsible) showRow = false
        else if (filters.highPriority && !task.highPriority) showRow = false
        else if (statusArray.length > 0 && !statusArray.includes(task.status)) showRow = false
        else if (filters.overdue || filters.deadline) {
            showRow = filterByDeadline(filters, task.deadline ? new Date(task.deadline) : null)
        } else if (filters.search) showRow = search(filters.search, task)

        return showRow
    })

    return tasksFiltered.sort((a, b) => sort(a, b, sortColumn, sortOrder))
}

export const SaveTaskTags = (
    taskId: number,
    tags: MtTaskTag[],
) => {
    const tagData: SaveMtTaskTagType = {
        taskId,
        tags,
    }
    return POST(
        "/mtTaskTag/save",
        JSON.stringify(tagData),
    )
}

export const LoadTaskAttachments = (taskId: number) => (
    GET(`/mtTaskAttachment/taskId/${taskId}`)
    .then((attachments: MtTaskAttachment[]) => attachments)
)

export const SaveTaskAttachments = (
    taskId: number,
    attachments: MtTaskAttachment[],
) => {
    const attachmentsData: SaveMtTaskAttachmentType = {
        taskId,
        attachments,
    }
    return POST(
        "/mtTaskAttachment/save",
        JSON.stringify(attachmentsData),
    )
}
